import {
    IAttendanceAuditRecord,
    IAttendanceCommandHistoryItem,
    IAttendanceConfiguration,
    IAttendanceConsumerConfiguration,
    IAttendanceOperationalSettings,
    IAttendanceSessionPair,
    IAttendanceSessionSettings,
    IAttendanceTimingBands,
    IAttendanceWritebackSummary,
} from "../models/AttendanceConfiguration";
import {
    dfeAttendanceCodeDefinitions,
} from "../app/common/data/AttendanceCodeCatalogue";
import { defaultAttendanceSessionDefinitions } from "../app/common/data/AttendanceSessionDefinitions";
import {
    copySessionPair,
    getProtectedAttendanceCodes,
    getChangedSections,
    normaliseAttendanceCodes,
    synchroniseProviderSessionDurationHints,
    toConsumerConfiguration,
    validateAttendanceConfiguration,
} from "../pages/establishments/config/attendance/attendance-utils";

export const attendanceYearGroups = [
    "Reception",
    "Year 1",
    "Year 2",
    "Year 3",
    "Year 4",
    "Year 5",
    "Year 6",
    "Year 7",
    "Year 8",
    "Year 9",
    "Year 10",
    "Year 11",
    "Year 12",
    "Year 13",
];

export { dfeAttendanceCodeDefinitions, supportedDfeAttendanceCodes } from "../app/common/data/AttendanceCodeCatalogue";

function clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
}

function formatDate(value: Date): string {
    return value.toISOString().slice(0, 10);
}

function isValidTime(value: string): boolean {
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

function createSession(
    registrationStart: string,
    providerSessionEnd: string,
    providerSessionDurationHintMinutes: number,
    timingBands: IAttendanceTimingBands,
    timetableStart: string = registrationStart,
): IAttendanceSessionSettings {
    return {
        registrationStart,
        timetableStart,
        providerSessionEnd,
        providerSessionDurationHintMinutes,
        timingBands,
    };
}

function createDefaultSessions(): IAttendanceSessionPair {
    return {
        am: createSession("08:45", "09:05", 20, {
            preSessionGraceMinutes: 0,
            onTimeGraceMinutes: 5,
            earlyLateBandMinutes: 10,
            lateLateBandMinutes: 5,
        }, "09:05"),
        pm: createSession("13:00", "13:15", 15, {
            preSessionGraceMinutes: 0,
            onTimeGraceMinutes: 5,
            earlyLateBandMinutes: 5,
            lateLateBandMinutes: 5,
        }, "13:00"),
    };
}

function formatSessionTiming(settings: IAttendanceSessionSettings): string {
    const timingBands = settings.timingBands;
    const bandSummary = timingBands
        ? `pre ${timingBands.preSessionGraceMinutes} min; on time ${timingBands.onTimeGraceMinutes} min; early late ${timingBands.earlyLateBandMinutes} min; late late ${timingBands.lateLateBandMinutes} min`
        : "arrival bands not configured";

    return `${settings.registrationStart}-${settings.providerSessionEnd}; ${bandSummary}`;
}

function createAuditValue(section: string, configuration: IAttendanceConfiguration): string {
    switch (section) {
        case "Session timing":
            return `Default AM ${formatSessionTiming(configuration.defaultSessions.am)}; default PM ${formatSessionTiming(configuration.defaultSessions.pm)}`;
        case "Code catalogue":
            return `${configuration.catalogue.dfeCodes.length} enabled code(s); ${configuration.overwritableMarks.length} overwrite exception(s)`;
        case "Attendance task":
            return `Daily Attendance timetable sync at ${configuration.operations.dailySyncTime}`;
        default:
            return "Configuration changed";
    }
}

export default class AttendanceConfigurationService {
    private readonly configurations = new Map<string, IAttendanceConfiguration>();
    private readonly audits = new Map<string, IAttendanceAuditRecord[]>();
    private readonly removedAttendanceWritebackTasks = new Set<string>();

    public async GetConfiguration(establishmentId: string): Promise<IAttendanceConfiguration> {
        const configuration = this.getOrCreateConfiguration(establishmentId);
        return clone(configuration);
    }

    public async SaveConfiguration(
        establishmentId: string,
        configuration: IAttendanceConfiguration,
    ): Promise<IAttendanceConfiguration> {
        const previous = this.configurations.get(establishmentId) ?? null;
        const normalised = this.normaliseConfiguration({
            ...clone(configuration),
            establishmentId,
            isConfigured: true,
        });
        const validationErrors = validateAttendanceConfiguration(normalised, formatDate(new Date()));

        if (validationErrors.length > 0) {
            throw new Error(validationErrors.join(" "));
        }

        const saved = {
            ...normalised,
            updatedAt: new Date().toISOString(),
            updatedBy: "support.admin@miscentral.local",
        };
        const changedSections = getChangedSections(previous, saved);

        this.configurations.set(establishmentId, saved);
        this.appendAuditRecords(establishmentId, previous, saved, changedSections);

        return clone(saved);
    }

    public async UpdateAttendanceWritebackTaskSchedule(
        establishmentId: string,
        dailySyncTime: string,
    ): Promise<IAttendanceConfiguration> {
        if (!isValidTime(dailySyncTime)) {
            throw new Error("Attendance schedule must be a valid time.");
        }

        const previous = this.getOrCreateConfiguration(establishmentId);
        const saved: IAttendanceConfiguration = {
            ...clone(previous),
            operations: {
                ...previous.operations,
                dailySyncTime,
            },
            updatedAt: new Date().toISOString(),
            updatedBy: "support.admin@miscentral.local",
        };

        this.configurations.set(establishmentId, saved);
        this.removedAttendanceWritebackTasks.delete(establishmentId);
        this.appendAuditRecords(establishmentId, previous, saved, ["Attendance task"]);

        return clone(saved);
    }

    public async DeleteAttendanceWritebackTask(establishmentId: string): Promise<void> {
        const configuration = this.getOrCreateConfiguration(establishmentId);

        if (this.removedAttendanceWritebackTasks.has(establishmentId)) {
            return;
        }

        this.removedAttendanceWritebackTasks.add(establishmentId);
        const existing = this.audits.get(establishmentId) ?? [];
        this.audits.set(establishmentId, [
            {
                id: `audit-${establishmentId}-${Date.now()}-attendance-writeback-task-deleted`,
                actor: "support.admin@miscentral.local",
                changedAt: new Date().toISOString(),
                changedSection: "Attendance task",
                beforeValue: createAuditValue("Attendance task", configuration),
                afterValue: "Attendance task removed.",
                action: "Deleted",
                establishmentName: "Northfield Academy",
            },
            ...existing,
        ]);
    }

    public async GetWritebackSummary(establishmentId: string): Promise<IAttendanceWritebackSummary[]> {
        this.getOrCreateConfiguration(establishmentId);

        return [
            {
                id: "summary-1",
                attendanceCode: "L",
                writtenAt: "2026-08-20T08:58:00.000Z",
                batchId: "AM-20260820-01",
                session: "AM",
                attemptedCount: 14,
                successfulCount: 14,
                failedCount: 0,
                status: "completed",
            },
            {
                id: "summary-2",
                attendanceCode: "N",
                writtenAt: "2026-08-20T13:08:00.000Z",
                batchId: "PM-20260820-01",
                session: "PM",
                attemptedCount: 8,
                successfulCount: 7,
                failedCount: 1,
                status: "partial",
            },
            {
                id: "summary-3",
                attendanceCode: "/",
                writtenAt: "2026-08-19T08:57:00.000Z",
                batchId: "AM-20260819-01",
                session: "AM",
                attemptedCount: 22,
                successfulCount: 22,
                failedCount: 0,
                status: "completed",
            },
        ];
    }

    public async GetCommandHistory(establishmentId: string): Promise<IAttendanceCommandHistoryItem[]> {
        this.getOrCreateConfiguration(establishmentId);

        return [
            {
                id: "command-1",
                commandType: "WriteAttendanceMark",
                status: "completed",
                resultCode: "200",
                resultMessage: "Mark written to Bromcom.",
                createdAt: "2026-08-20T08:57:05.000Z",
                processedAt: "2026-08-20T08:57:08.000Z",
                batchId: "AM-20260820-01",
                session: "AM",
                targetDate: "2026-08-20",
                submittedBy: "attendance-sync@miscentral.local",
                studentResults: [
                    {
                        id: "command-1-student-1",
                        studentName: "Amina Rahman",
                        personRecordId: "crbc-amina-rahman",
                        misId: "BROM-12001",
                        admissionNumber: "12001",
                        upn: "A123456789012",
                        yearGroup: "Year 7",
                        registrationClass: "7A",
                        session: "AM",
                        attemptedCode: "L",
                        codeWritten: "L",
                        status: "success",
                        resultCode: "200",
                        message: "Mark written to Bromcom.",
                    },
                    {
                        id: "command-1-student-2",
                        studentName: "Theo James",
                        personRecordId: "crbc-theo-james",
                        misId: "BROM-12002",
                        admissionNumber: "12002",
                        upn: "A123456789013",
                        yearGroup: "Year 7",
                        registrationClass: "7B",
                        session: "AM",
                        attemptedCode: "L",
                        codeWritten: "L",
                        status: "success",
                        resultCode: "200",
                        message: "Mark written to Bromcom.",
                    },
                ],
            },
            {
                id: "command-2",
                commandType: "WriteAttendanceMark",
                status: "failed",
                resultCode: "409",
                resultMessage: "Existing mark is protected by the attendance policy.",
                createdAt: "2026-08-20T13:07:02.000Z",
                processedAt: "2026-08-20T13:07:04.000Z",
                batchId: "PM-20260820-01",
                session: "PM",
                targetDate: "2026-08-20",
                submittedBy: "attendance-sync@miscentral.local",
                studentResults: [
                    {
                        id: "command-2-student-1",
                        studentName: "Leila Patel",
                        personRecordId: "crbc-leila-patel",
                        misId: "BROM-12003",
                        admissionNumber: "12003",
                        upn: "A123456789014",
                        yearGroup: "Year 8",
                        registrationClass: "8A",
                        session: "PM",
                        attemptedCode: "N",
                        codeWritten: "N",
                        status: "failed",
                        resultCode: "409",
                        message: "Existing mark is protected by the attendance policy.",
                    },
                    {
                        id: "command-2-student-2",
                        studentName: "Max Turner",
                        personRecordId: "crbc-max-turner",
                        misId: "BROM-12004",
                        admissionNumber: "12004",
                        upn: "A123456789015",
                        yearGroup: "Year 8",
                        registrationClass: "8B",
                        session: "PM",
                        attemptedCode: "N",
                        codeWritten: "N",
                        status: "success",
                        resultCode: "200",
                        message: "Mark written to Bromcom.",
                    },
                ],
            },
            {
                id: "command-3",
                commandType: "RefreshAttendanceLookups",
                status: "pending",
                resultCode: "QUEUED",
                resultMessage: "Awaiting the configured Attendance schedule.",
                createdAt: "2026-08-20T16:05:00.000Z",
                processedAt: null,
                batchId: "SYNC-20260820-01",
                session: "PM",
                targetDate: "2026-08-20",
                submittedBy: "attendance-sync@miscentral.local",
                studentResults: [],
            },
        ];
    }

    public async GetAuditHistory(establishmentId: string): Promise<IAttendanceAuditRecord[]> {
        this.getOrCreateConfiguration(establishmentId);
        return clone(this.audits.get(establishmentId) ?? []);
    }

    public async GetAllAuditHistory(): Promise<IAttendanceAuditRecord[]> {
        if (this.audits.size === 0) {
            this.getOrCreateConfiguration("audit-demo-establishment");
        }

        return clone(
            Array.from(this.audits.entries())
                .flatMap(([establishmentId, records]) => records.map((record) => ({
                    ...record,
                    establishmentName: record.establishmentName ?? `Establishment ${establishmentId.slice(0, 8)}`,
                })))
                .sort((left, right) => right.changedAt.localeCompare(left.changedAt)),
        );
    }

    public async GetConsumerConfiguration(establishmentId: string): Promise<IAttendanceConsumerConfiguration> {
        const configuration = this.getOrCreateConfiguration(establishmentId);
        return toConsumerConfiguration(configuration);
    }

    private getOrCreateConfiguration(establishmentId: string): IAttendanceConfiguration {
        const existing = this.configurations.get(establishmentId);

        if (existing) {
            return existing;
        }

        const created = this.createMockConfiguration(establishmentId);
        this.configurations.set(establishmentId, created);
        this.audits.set(establishmentId, [
            {
                id: `audit-initial-${establishmentId}`,
                actor: "provisioning@miscentral.local",
                changedAt: "2026-08-19T15:30:00.000Z",
                changedSection: "Initial policy",
                beforeValue: "No policy configured",
                afterValue: "Attendance policy created.",
                action: "Created",
                establishmentName: "Northfield Academy",
            },
        ]);

        return created;
    }

    private createMockConfiguration(establishmentId: string): IAttendanceConfiguration {
        const defaultSessions = createDefaultSessions();
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);

        return {
            establishmentId,
            isConfigured: true,
            sessionDefinitions: [...defaultAttendanceSessionDefinitions],
            defaultSessions,
            yearGroupSettings: attendanceYearGroups.map((yearGroup) => {
                if (yearGroup === "Year 7") {
                    return {
                        yearGroup,
                        usesDefault: false,
                        overriddenSessionIds: ["am"],
                        am: createSession("08:40", "09:00", 20, {
                            preSessionGraceMinutes: 0,
                            onTimeGraceMinutes: 5,
                            earlyLateBandMinutes: 10,
                            lateLateBandMinutes: 5,
                        }, "08:45"),
                        pm: createSession("13:00", "13:15", 15, {
                            preSessionGraceMinutes: 0,
                            onTimeGraceMinutes: 5,
                            earlyLateBandMinutes: 5,
                            lateLateBandMinutes: 5,
                        }, "13:00"),
                    };
                }

                return {
                    yearGroup,
                    usesDefault: true,
                    ...copySessionPair(defaultSessions),
                };
            }),
            catalogue: {
                dfeCodes: dfeAttendanceCodeDefinitions
                    .filter((definition) => definition.defaultEnabled)
                    .map((definition) => definition.code),
                customCodes: [],
                allowCustomCodes: false,
            },
            protectedCodes: getProtectedAttendanceCodes([], []),
            overwritableMarks: [],
            markSnapshot: {
                isAvailable: true,
                observedAt: "2026-08-20T07:45:00.000Z",
                marks: [" ", "/", "L", "N", "O", "U"],
            },
            operations: {
                liveDate: formatDate(tomorrow),
                operationalMarkRetentionDays: 7,
                commandHistoryRetentionDays: 30,
                dailySyncTime: "05:30",
            },
            updatedAt: "2026-08-20T07:40:00.000Z",
            updatedBy: "provisioning@miscentral.local",
        };
    }

    private normaliseConfiguration(configuration: IAttendanceConfiguration): IAttendanceConfiguration {
        const configurationWithDerivedDurationHints = synchroniseProviderSessionDurationHints(configuration);
        const legacyConfiguration = { ...configurationWithDerivedDurationHints } as IAttendanceConfiguration & {
            connection?: unknown;
        };
        delete legacyConfiguration.connection;
        const operations = { ...legacyConfiguration.operations } as IAttendanceOperationalSettings & {
            writebackEnabled?: unknown;
            assumePmPresentForActiveSignIn?: unknown;
        };
        delete operations.writebackEnabled;
        delete operations.assumePmPresentForActiveSignIn;
        const catalogue = {
            ...configurationWithDerivedDurationHints.catalogue,
            dfeCodes: normaliseAttendanceCodes(configurationWithDerivedDurationHints.catalogue.dfeCodes),
            customCodes: normaliseAttendanceCodes(configurationWithDerivedDurationHints.catalogue.customCodes),
        };
        const overwritableMarks = normaliseAttendanceCodes(configurationWithDerivedDurationHints.overwritableMarks);

        return {
            ...legacyConfiguration,
            catalogue,
            yearGroupSettings: configurationWithDerivedDurationHints.yearGroupSettings.map((settings) => {
                const legacySettings = { ...settings } as typeof settings & {
                    assumePmPresentForActiveSignInOverride?: unknown;
                };
                delete legacySettings.assumePmPresentForActiveSignInOverride;
                return legacySettings;
            }),
            operations,
            protectedCodes: getProtectedAttendanceCodes(catalogue.customCodes, overwritableMarks),
            overwritableMarks,
        };
    }

    private appendAuditRecords(
        establishmentId: string,
        previous: IAttendanceConfiguration | null,
        saved: IAttendanceConfiguration,
        changedSections: string[],
    ): void {
        if (changedSections.length === 0) {
            return;
        }

        const existing = this.audits.get(establishmentId) ?? [];
        const records: IAttendanceAuditRecord[] = changedSections.map((changedSection, index) => {
            return {
                id: `audit-${establishmentId}-${Date.now()}-${index}`,
                actor: "support.admin@miscentral.local",
                changedAt: saved.updatedAt ?? new Date().toISOString(),
                changedSection,
                beforeValue: previous ? createAuditValue(changedSection, previous) : "Not configured",
                afterValue: createAuditValue(changedSection, saved),
                action: "Updated",
                establishmentName: "Northfield Academy",
            };
        });

        this.audits.set(establishmentId, [...records, ...existing]);
    }
}